import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { normalizeDashboardLayout } from '@/utils/dashboard.layout';
import type {
  DashboardLayout,
  DashboardType,
  UserDashboardSettings,
} from '@/utils/dashboard.types';

import { fetchDashboardSettings, saveDashboardSettings } from './dashboard.api';

export const useDashboardSettingsQuery = (userId: string | undefined) =>
  useQuery({
    queryKey: ['settings', userId],
    queryFn: () => fetchDashboardSettings(userId!),
    enabled: !!userId,
    staleTime: Infinity,
  });

/**
 * The layout to render for this account, reconciled against the role's
 * defaults. Reconciling is read-only on purpose: a stored layout that is merely
 * out of date is repaired in memory, never overwritten on the server behind the
 * user's back.
 */
export const useDashboardLayout = (
  userId: string | undefined,
  dashboardType: DashboardType,
): DashboardLayout => {
  const { data: settings } = useDashboardSettingsQuery(userId);
  const stored = settings?.dashboards?.[dashboardType];

  return useMemo(() => normalizeDashboardLayout(stored, dashboardType), [stored, dashboardType]);
};

export const useSaveDashboardSettingsMutation = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: UserDashboardSettings) => saveDashboardSettings(userId!, settings),
    onSuccess: (_, settings) => {
      queryClient.setQueryData(['settings', userId], settings);
      void queryClient.invalidateQueries({ queryKey: ['settings', userId] });
    },
  });
};
